import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  ChevronRight,
  Lock,
  Menu,
  PlayCircle,
  Sparkles,
  X,
} from 'lucide-react';
import GastifyBrand from '../../components/public/GastifyBrand';
import { academiaCategorias, todosLosModulos } from './moduloData';
import { CRM_ORG_MODULE_ALIAS_MAP } from '../../utils/accessModules';
import { useAuth } from '../../context/AuthContext';
import { logOnboardingEvent } from '../../utils/onboardingTelemetry';

export default function AcademiaViewer({ orgModules }) {
  const { slug } = useParams();
  const { profile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const hasOrgModuleAccess = (moduleId) => {
    if (!moduleId) return true;
    if (!orgModules) return true;

    const orgModulesSet = new Set(orgModules);
    const allowedModuleIds = [
      moduleId,
      ...(CRM_ORG_MODULE_ALIAS_MAP[moduleId] || []),
    ];
    return allowedModuleIds.some((candidateId) => orgModulesSet.has(candidateId));
  };

  const visibleCategories = useMemo(
    () =>
      academiaCategorias
        .map((category) => ({
          ...category,
          modulos: category.modulos.filter((moduleItem) =>
            hasOrgModuleAccess(moduleItem.requiredModule),
          ),
        }))
        .filter((category) => category.modulos.length > 0),
    [orgModules],
  );

  const accessibleModules = useMemo(
    () =>
      todosLosModulos.filter((moduleItem) =>
        hasOrgModuleAccess(moduleItem.requiredModule),
      ),
    [orgModules],
  );

  const moduleInfo =
    accessibleModules.find((moduleItem) => moduleItem.id === slug) ||
    accessibleModules[0] ||
    todosLosModulos[0];

  const Icon = moduleInfo.icon;
  const hasAccess = hasOrgModuleAccess(moduleInfo.requiredModule);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsSidebarOpen(false);
  }, [slug]);

  // Embudo de activacion: registra la apertura de un curso. Era el unico hito
  // del funnel sin instrumentar; permite medir la adopcion real de la Academia
  // y cruzarla con la activacion (first_real_sale).
  // Dedup por curso+usuario: el profile llega async, asi que sin el ref el
  // efecto se re-dispararia al hidratarse y contaria la misma apertura dos veces.
  const loggedCourseRef = useRef(null);
  useEffect(() => {
    if (!moduleInfo?.id || !hasAccess) return;
    const key = `${moduleInfo.id}:${profile?.id || 'anon'}`;
    if (loggedCourseRef.current === key) return;
    loggedCourseRef.current = key;
    logOnboardingEvent({
      event: 'academy_course_opened',
      organizationId: profile?.organization_id || null,
      userId: profile?.id || null,
      metadata: { moduleId: moduleInfo.id, title: moduleInfo.title || null },
    });
  }, [moduleInfo?.id, moduleInfo?.title, hasAccess, profile?.organization_id, profile?.id]);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[var(--gst-cream-50)] px-4 py-10">
        <div className="mx-auto flex max-w-xl flex-col items-center rounded-[1.75rem] border border-[color:var(--gst-border)] bg-[var(--surface)] p-8 text-center shadow-[var(--gst-shadow-md)]">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--gst-danger-bg)] text-[var(--gst-danger)]">
            <Lock size={34} />
          </div>
          <h1 className="mt-6 text-3xl font-black text-[var(--gst-graphite-900)]">
            Acceso restringido
          </h1>
          <p className="mt-3 text-base leading-relaxed text-[var(--gst-graphite-700)]">
            Este manual no está habilitado para la organización actual. Si debería estar disponible, revisa los módulos activos o consulta con el administrador.
          </p>
          <Link
            to="/sistema/academia"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-[var(--gst-mint-500)] px-5 py-3 text-sm font-black text-white transition hover:bg-[var(--gst-mint-600)]"
          >
            <ArrowLeft size={16} />
            Volver a la academia
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--gst-cream-50)] text-[var(--gst-graphite-900)]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-[rgba(16,185,129,0.10)] blur-3xl" />
        <div className="absolute bottom-10 right-0 h-80 w-80 rounded-full bg-[rgba(254,215,170,0.18)] blur-3xl" />
      </div>

      <nav className="sticky top-0 z-40 border-b border-[color:var(--gst-border)] bg-[rgba(251,248,241,0.94)] backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between gap-3 px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsSidebarOpen((currentValue) => !currentValue)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[color:var(--gst-border)] bg-[var(--surface)] text-[var(--gst-graphite-700)] lg:hidden"
            >
              {isSidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>

            <Link to="/sistema/academia" className="min-w-0">
              <GastifyBrand iconSize={36} subtitle="Centro de aprendizaje" />
            </Link>
          </div>

          <Link
            to="/sistema/academia"
            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--gst-border)] bg-[var(--surface)] px-4 py-2 text-sm font-bold text-[var(--gst-graphite-700)] transition hover:border-[var(--gst-mint-500)] hover:text-[var(--gst-mint-700)]"
          >
            <ArrowLeft size={16} />
            Volver al índice
          </Link>
        </div>
      </nav>

      <div className="relative flex min-h-[calc(100vh-4rem)]">
        {isSidebarOpen ? (
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 z-30 bg-[rgba(31,36,33,0.18)] backdrop-blur-sm lg:hidden"
          />
        ) : null}

        <aside
          className={`fixed inset-y-16 left-0 z-40 w-[20rem] border-r border-[color:var(--gst-border)] bg-[var(--gst-surface)] shadow-xl transition-transform duration-300 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:translate-x-0 lg:shadow-none ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="custom-scrollbar flex h-full flex-col overflow-y-auto p-5">
            <div className="rounded-[1.25rem] border border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] p-4">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[var(--gst-mint-700)]">
                Navegación
              </p>
              <h2 className="mt-2 text-lg font-black text-[var(--gst-graphite-900)]">
                Módulos de estudio
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-[var(--gst-graphite-700)]">
                Recorre cada guía con la misma estructura visual del sistema principal.
              </p>
            </div>

            <div className="mt-5 space-y-6">
              {visibleCategories.map((category) => (
                <div key={category.titulo}>
                  <h3 className="px-1 text-[10px] font-black uppercase tracking-[0.22em] text-[var(--gst-graphite-500)]">
                    {category.titulo}
                  </h3>
                  <div className="mt-2 space-y-1.5">
                    {category.modulos.map((moduleItem) => {
                      const isActive = moduleItem.id === moduleInfo.id;

                      return (
                        <Link
                          key={moduleItem.id}
                          to={`/sistema/academia/${moduleItem.id}`}
                          className={`flex items-center gap-3 rounded-md border px-3 py-3 text-sm font-semibold transition ${
                            isActive
                              ? 'border-[var(--gst-mint-500)] bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)]'
                              : 'border-transparent text-[var(--gst-graphite-700)] hover:border-[color:var(--gst-border)] hover:bg-[var(--gst-cream-50)]'
                          }`}
                        >
                          <moduleItem.icon
                            size={16}
                            className={isActive ? 'text-[var(--gst-mint-700)]' : 'text-[var(--gst-graphite-500)]'}
                          />
                          <span className="line-clamp-1">{moduleItem.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-6 md:px-6 md:py-8">
          <div className="mx-auto max-w-5xl">
            <section className="rounded-[1.75rem] border border-[color:var(--gst-border)] bg-[var(--surface)] p-6 shadow-[var(--gst-shadow-md)] md:p-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-[var(--gst-mint-100)] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-[var(--gst-mint-700)]">
                <Sparkles size={14} />
                Guía Gastify
              </div>
              <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                <div className="max-w-3xl">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)]">
                      <Icon size={22} />
                    </div>
                    <span className="rounded-full bg-[var(--gst-cream-50)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[var(--gst-graphite-500)]">
                      {moduleInfo.requiredModule || 'Base'}
                    </span>
                  </div>
                  <h1 className="mt-5 text-4xl font-black leading-[1] tracking-[-0.04em] text-[var(--gst-graphite-900)] md:text-5xl">
                    {moduleInfo.title}
                  </h1>
                  <p className="mt-4 text-lg leading-relaxed text-[var(--gst-graphite-700)]">
                    {moduleInfo.description}
                  </p>
                </div>

                <div className="rounded-[1.25rem] border border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] p-4 md:w-64">
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[var(--gst-graphite-500)]">
                    Contenido
                  </p>
                  <p className="mt-2 text-3xl font-black text-[var(--gst-graphite-900)]">
                    {moduleInfo.pasos?.length || 0}
                  </p>
                  <p className="mt-1 text-sm text-[var(--gst-graphite-700)]">
                    pasos explicados para operar sin diseño legacy ni dudas de navegación.
                  </p>
                </div>
              </div>
            </section>

            <section className="mt-6 rounded-[1.5rem] border border-[color:var(--gst-border)] bg-[rgba(255,255,255,0.72)] p-5 shadow-[var(--gst-shadow-sm)] backdrop-blur-sm md:p-6">
              <div className="flex aspect-video w-full flex-col items-center justify-center rounded-[1.25rem] border border-dashed border-[var(--gst-border-strong)] bg-[var(--gst-cream-50)] text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)]">
                  <PlayCircle size={30} />
                </div>
                <p className="mt-4 text-sm font-black uppercase tracking-[0.2em] text-[var(--gst-graphite-500)]">
                  Video guía
                </p>
                <p className="mt-2 text-base text-[var(--gst-graphite-700)]">
                  Próximamente vamos a sumar este recorrido en formato audiovisual.
                </p>
              </div>
            </section>

            <section className="mt-6 rounded-[1.5rem] border border-[color:var(--gst-border)] bg-[var(--surface)] p-5 shadow-[var(--gst-shadow-sm)] md:p-6">
              <div className="flex items-center gap-2">
                <BookOpen size={18} className="text-[var(--gst-mint-700)]" />
                <h2 className="text-2xl font-black text-[var(--gst-graphite-900)]">
                  Paso a paso
                </h2>
              </div>

              <div className="mt-6 space-y-4">
                {moduleInfo.pasos && moduleInfo.pasos.length > 0 ? (
                  moduleInfo.pasos.map((paso, index) => (
                    <div
                      key={`${moduleInfo.id}-paso-${index + 1}`}
                      className="flex gap-4 rounded-[1.1rem] border border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] p-4 md:p-5"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[var(--gst-mint-500)] text-sm font-black text-white">
                        {index + 1}
                      </div>
                      <p className="pt-1 text-[15px] leading-relaxed text-[var(--gst-graphite-700)]">
                        {paso}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm italic text-[var(--gst-graphite-500)]">
                    No hay pasos definidos para este módulo por el momento.
                  </p>
                )}
              </div>
            </section>

            <div className="mt-6 flex justify-end">
              <Link
                to="/sistema/academia"
                className="inline-flex items-center gap-2 rounded-md bg-[var(--gst-mint-500)] px-5 py-3 text-sm font-black text-white transition hover:bg-[var(--gst-mint-600)]"
              >
                Regresar a la academia
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
